import { computed, Injectable, signal } from '@angular/core';
import { INotificationMessage, NotificationMessage } from '../models/notification.message.model';
import { StorageService } from '../../../core/data-access/storages/session-local-storages/session-local-storage.service';
import { Logger } from '../../../core/utilities/logging/logger';
import { UserContext } from '../../../user-management/auth-management/authentication/models/user-context.model';
import { UserContextService } from '../../../user-management/auth-management/authentication/services/user-context.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private static readonly STORAGE_KEY = 'notifications';

    notifications = signal<NotificationMessage<any>[]>([]);

    unReadNotifications = computed(() => {
        return this.notifications().filter(notification => !notification.isReaded);
    });

    unreadCount = computed(() => {
        return this.unReadNotifications().length;
    });

    constructor() {
        this.loadNotificationsFromStorage();
        this.saveNotificationsToStorage();
    }

    getAllNotifications(): NotificationMessage<any>[] {
        return NotificationMessage.createMany(this.notifications());
    }

    addNotification<TContent>(message: INotificationMessage<TContent>): void {
        const exists = this.notifications().some(notification => notification.id === message.id);

        if (exists) {
            return;
        }

        this.notifications.update(notifications => [NotificationMessage.create(message), ...notifications]);
        this.sortNotificationsByDate();
        this.saveNotificationsToStorage();
    }

    markNotificationAsRead(id: string): void {
        this.notifications.update(notifications => {
            const notification = notifications.find(n => n.id === id);
            if (notification) {
                notification.markAsReaded();
            }
            return [...notifications];
        });
        this.saveNotificationsToStorage();
    }

    markAllNotificationsAsRead(): void {
        this.notifications.update(notifications => notifications.map(n => n.markAsReaded()));
        this.saveNotificationsToStorage();
    }

    clearNotification(id: string): void {
        this.notifications.update(notifications => notifications.filter(n => n.id !== id));
        this.saveNotificationsToStorage();
    }

    clearAllNotifications(): void {
        this.notifications.set([]);
        this.saveNotificationsToStorage();
    }

    getNotificationContent<T>(id: string): T | undefined {
        const notification = this.notifications().find(n => n.id === id);
        return notification ? (notification.content as T) : undefined;
    }

    getNotificationsByType(type: string): NotificationMessage<any>[] {
        return this.notifications().filter(notification => notification.isOfType(type));
    }

    getNotificationCount(): number {
        return this.notifications().length;
    }

    sortNotificationsByDate(descending: boolean = true): void {
        this.notifications.update(notifications =>
            [...notifications].sort((a, b) => {
                let notificationA = NotificationMessage.create(a);
                let notificationB = NotificationMessage.create(b);

                return descending
                    ? notificationB.createdOnDate.getTime() - notificationA.createdOnDate.getTime()
                    : notificationA.createdOnDate.getTime() - notificationB.createdOnDate.getTime();
            })
        );
    }

    private loadNotificationsFromStorage(): void {
        const storedNotifications = StorageService.getItem<INotificationMessage<any>[]>(this.getNotificaitonKey()) || [];
        this.notifications.set(NotificationMessage.createMany(storedNotifications));
    }

    private saveNotificationsToStorage(): void {
        const notifications = this.notifications();
        Logger.info('Notifications to be saved', notifications);
        StorageService.setItem<any>(this.getNotificaitonKey(), notifications, true);
    }

    private getNotificaitonKey(): string {
        return `User-${UserContextService.getUserContext()?.id}-${NotificationService.STORAGE_KEY}`;
    }
}
